import { Event, domain } from '@m/rum-client/events';

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

type Handler = (evt: Event) => void;
type Indexable<T> = T & Record<string, unknown>;

interface JQueryMethodUsage {
  method: string;
  count: number;
}

// ----------------------------------------------------------------------
// EVENTS
// ----------------------------------------------------------------------

class JQueryFunctionCallEvent extends Event {
  constructor(method: string, count: number) {
    super({
      name: 'event_jquery_function_call',
      domain: domain.SESSION,
      attributes: { method, count: String(count), label: 'jQuery function call' }
    });
  }
}

// ----------------------------------------------------------------------
// HELPERS
// ----------------------------------------------------------------------

const jqWindow = window as Window &
  typeof globalThis & {
    $: JQueryStatic;
    jQuery: JQueryStatic;
  };

function isJQuery(value: unknown): value is JQueryStatic {
  return (
    typeof value === 'function' &&
    typeof (value as { fn?: unknown }).fn === 'object' &&
    (value as { fn?: unknown }).fn !== null
  );
}

// ----------------------------------------------------------------------
// DETECTOR
// ----------------------------------------------------------------------

export class JQueryDetector {
  private static instance_: JQueryDetector;

  private originalJQuery?: JQueryStatic;
  private methodUsage = new Map<string, number>();
  private isInstrumented = false;

  public static get instance(): JQueryDetector {
    if (!JQueryDetector.instance_) {
      JQueryDetector.instance_ = new JQueryDetector();
    }

    return JQueryDetector.instance_;
  }

  public static resetInstance(jQueryInstance?: JQueryStatic) {
    JQueryDetector.instance_ = new JQueryDetector(jQueryInstance);
  }

  private constructor(jQueryInstance?: JQueryStatic) {
    this.originalJQuery = jQueryInstance ?? jqWindow.$ ?? jqWindow.jQuery;
  }

  // ---- Tracking ----------------------------------------------------

  private track(name: string) {
    this.methodUsage.set(name, (this.methodUsage.get(name) || 0) + 1);
  }

  /** Wraps `fn` so every call is tracked under `name`. */
  private tracked<F extends (...args: never[]) => unknown>(name: string, fn: F) {
    const self = this;

    return function (this: ThisParameterType<F>, ...args: Parameters<F>): ReturnType<F> {
      self.track(name);
      return fn.apply(this, args) as ReturnType<F>;
    };
  }

  // ---- Instrumentation ---------------------------------------------

  /** Wraps every method on a `$()` result via Proxy. */
  private proxyResult(result: JQuery<HTMLElement>): JQuery<HTMLElement> {
    if (typeof result !== 'object' || result === null) {
      return result;
    }

    const self = this;

    return new Proxy(result, {
      get(target, prop: string | symbol) {
        const value = Reflect.get(target, prop);

        if (typeof value === 'function') {
          return self.tracked(String(prop), value.bind(target));
        }

        return value;
      }
    });
  }

  /** Creates a tracked interceptor that replaces `window.$`. */
  private createInterceptor(jq: JQueryStatic): JQueryStatic {
    const interceptor = this.tracked('$', (...args: Parameters<JQueryStatic>) => {
      return this.proxyResult(jq.apply(null, args) as JQuery<HTMLElement>);
    }) as unknown as JQueryStatic;

    Object.setPrototypeOf(interceptor, jq);

    const src = jq as Indexable<JQueryStatic>;
    const tgt = interceptor as Indexable<JQueryStatic>;

    for (const prop of Object.keys(src)) {
      const value = src[prop];

      if (typeof value === 'function' && prop !== 'fn') {
        tgt[prop] = this.tracked(prop, value as (...args: never[]) => unknown);
      } else {
        tgt[prop] = value;
      }
    }

    (tgt as { -readonly [K in keyof JQueryStatic]: JQueryStatic[K] }).fn = jq.fn;

    return interceptor;
  }

  // ---- Public API --------------------------------------------------

  public init() {
    if (this.isInstrumented) {
      return;
    }

    const jq = jqWindow.$ ?? jqWindow.jQuery ?? this.originalJQuery;

    if (!isJQuery(jq)) {
      return;
    }

    this.originalJQuery = jq;
    this.isInstrumented = true;

    const interceptor = this.createInterceptor(jq);
    jqWindow.jQuery = interceptor;
    jqWindow.$ = interceptor;
  }

  public trackUsedAPIs(handler: Handler) {
    this.methodUsage.forEach((count, method) => {
      handler(new JQueryFunctionCallEvent(method, count));
    });
  }

  public reset() {
    if (this.originalJQuery) {
      jqWindow.$ = this.originalJQuery;
      jqWindow.jQuery = this.originalJQuery;
    }

    this.isInstrumented = false;
    this.methodUsage.clear();
  }

  public getUsageStats(): JQueryMethodUsage[] {
    return Array.from(this.methodUsage.entries())
      .map(([method, count]) => ({ method, count }))
      .sort((a, b) => b.count - a.count);
  }
}

export default JQueryDetector.instance;
